import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useAction } from '../../context';
import { Confirm, Failure, Field } from '../../bits';
import { Chip, Panel, Switch } from '../../kit';
import { useT } from '../../../messages';
import type { CatalogItem } from '../../../wire';
import { ItemOptions } from './ItemOptions';
import { ItemAddOns } from './ItemAddOns';
import { ItemPhotos } from './ItemPhotos';
import { headingMap, money, textOf, withLocale } from './text';

interface Draft {
  name: string;
  description: string;
  category: string;
  sortOrder: string;
  price: string;
  duration: string;
  /**
   * The two gaps as typed: `''` is "the house decides" (`null` on the wire,
   * migration 0064), `'0'` is none, a number is the item's own. Strings,
   * because a box mid-edit is not yet a number.
   */
  bufferBefore: string;
  bufferAfter: string;
  brand: string;
  /**
   * **Show on their website** (B10): a product's own `on_website`
   * (presentation — any status) or a treatment's `bookable_online` (commerce —
   * fenced once the business is live). One switch on screen, the column the
   * kind already answers the question with underneath.
   */
  onWebsite: boolean;
}

/** A buffer column as a box: `null` is a blank, a number is its digits. */
function bufferBox(stored: number | null | undefined): string {
  return stored === null || stored === undefined ? '' : String(stored);
}

/** …and back: a blank is `null` — "the house decides" — a `0` is `0`. */
function bufferWire(typed: string): number | null {
  return typed.trim() === '' ? null : Number(typed);
}

/**
 * **One editor for the chosen item** — the right half of the tab, replacing a
 * full inline form for every item at once.
 *
 * ## The fence is drawn here, and enforced somewhere else
 *
 * Migration 0050 lets the console write an item's **presentation** at any
 * status and its **commerce** (name, price, minutes, gaps, whether it is sold)
 * only while the business is being set up. This editor says so in one sentence
 * at the top and quiets the commerce boxes — and is careful not to *be* the
 * line: every save is sent, the database refuses the ones it should, and the
 * refusal renders like any other failure.
 *
 * ## Two calls and not one
 *
 * Presentation and commerce are two permissions and two audit actions. A save
 * that mixed them would be refused whole on a live business, taking a
 * legitimate reword down with an illegitimate price — so the reword goes first.
 *
 * ## Confirmed on a live business
 *
 * Once the business is past setting up, a save changes what guests read on its
 * site and what the AI agent quotes, the moment it lands (plan principle 5). The
 * dialog says that; while it is being set up nobody is reading yet, and the
 * save is a click.
 *
 * **Stop selling** is its own button with its own question, because it is the
 * one edit here that changes what can be booked tomorrow. There is no delete:
 * `appointments.item_id` is `ON DELETE RESTRICT` and last March's report still
 * names the treatment they stopped selling.
 *
 * ## An optioned item's headline is written, not typed
 *
 * `syncOptionHeadline` restates the item's price and minutes from the marked
 * length inside the same transaction as any length write. A box that let an
 * operator type over them would set a price the next length write silently
 * reverts, so the boxes go quiet and say where the numbers come from.
 */
export function ItemEditor({
  tenantId,
  item,
  locale,
  currency,
  commerceEditable,
  houseBuffers,
  siblings,
  uploadsConfigured,
  onChanged,
}: {
  tenantId: string;
  item: CatalogItem;
  locale: string;
  currency: string;
  commerceEditable: boolean;
  /** The house's gap, so a blank buffer box can say what it means. */
  houseBuffers: { before_min: number; after_min: number } | undefined;
  /** Every other scheduled service, for the add-on picker. */
  siblings: CatalogItem[];
  uploadsConfigured: boolean;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const selling = useAction();
  const [saved, setSaved] = useState(false);
  const [photos, setPhotos] = useState(false);
  const [asking, setAsking] = useState<'save' | 'selling' | null>(null);

  const current: Draft = {
    name: textOf(item.name, locale),
    description: textOf(item.description, locale),
    category: textOf(headingMap(item), locale),
    sortOrder: String(item.sort_order),
    price: money(item.price_cents),
    duration: item.scheduling === null ? '' : String(item.scheduling.duration_min),
    bufferBefore: bufferBox(item.scheduling?.buffer_before_min),
    bufferAfter: bufferBox(item.scheduling?.buffer_after_min),
    brand: item.retail?.brand ?? '',
    onWebsite:
      item.kind === 'stocked' ? item.on_website : (item.scheduling?.bookable_online ?? true),
  };
  const [draft, setDraft] = useState<Draft | null>(null);
  const value = draft ?? current;

  const patch = (next: Partial<Draft>): void => {
    action.clear();
    setSaved(false);
    setDraft({ ...value, ...next });
  };

  const optioned = item.options.length > 0;
  /** Which kind of website switch this item has, if any (B10). */
  const websiteSwitch: 'product' | 'service' | null =
    item.kind === 'stocked' ? 'product' : item.scheduling !== null ? 'service' : null;
  const priceCents = Math.round(Number(value.price) * 100);
  const priceValid = Number.isFinite(priceCents) && priceCents >= 0;
  const durationValid =
    item.scheduling === null ||
    (Number.isInteger(Number(value.duration)) && Number(value.duration) > 0);
  const bufferValid = (typed: string): boolean =>
    typed.trim() === '' || (Number.isInteger(Number(typed)) && Number(typed) >= 0);

  const presentationDirty =
    value.description !== current.description ||
    value.category !== current.category ||
    value.sortOrder !== current.sortOrder ||
    value.brand !== current.brand ||
    (websiteSwitch === 'product' && value.onWebsite !== current.onWebsite);
  const commerceDirty =
    value.name !== current.name ||
    value.price !== current.price ||
    value.duration !== current.duration ||
    value.bufferBefore !== current.bufferBefore ||
    value.bufferAfter !== current.bufferAfter ||
    (websiteSwitch === 'service' && value.onWebsite !== current.onWebsite);
  const dirty = presentationDirty || commerceDirty;
  const valid =
    priceValid &&
    durationValid &&
    bufferValid(value.bufferBefore) &&
    bufferValid(value.bufferAfter);

  const save = async (): Promise<void> => {
    const ok = await action.run(async () => {
      if (presentationDirty) {
        await api.setCatalogPresentation(tenantId, item.item_id, {
          ...(value.description === current.description
            ? {}
            : { description: withLocale(item.description, locale, value.description) }),
          ...(value.category === current.category
            ? {}
            : {
                category:
                  value.category.trim() === ''
                    ? null
                    : withLocale(headingMap(item), locale, value.category),
              }),
          ...(value.sortOrder === current.sortOrder ? {} : { sort_order: Number(value.sortOrder) }),
          ...(value.brand === current.brand ? {} : { brand: value.brand }),
          ...(websiteSwitch === 'product' && value.onWebsite !== current.onWebsite
            ? { on_website: value.onWebsite }
            : {}),
        });
      }
      if (commerceDirty) {
        await api.setCatalogCommerce(tenantId, item.item_id, {
          ...(value.name === current.name
            ? {}
            : { name: withLocale(item.name, locale, value.name) }),
          ...(value.price === current.price ? {} : { price_cents: priceCents }),
          ...(value.duration === current.duration || item.scheduling === null
            ? {}
            : { duration_min: Number(value.duration) }),
          // A blank box is `null` — "the house decides" — and a typed `0` is
          // `0`. Two different facts since 0064; the box is where they differ.
          ...(value.bufferBefore === current.bufferBefore || item.scheduling === null
            ? {}
            : { buffer_before_min: bufferWire(value.bufferBefore) }),
          ...(value.bufferAfter === current.bufferAfter || item.scheduling === null
            ? {}
            : { buffer_after_min: bufferWire(value.bufferAfter) }),
          ...(websiteSwitch === 'service' && value.onWebsite !== current.onWebsite
            ? { bookable_online: value.onWebsite }
            : {}),
        });
      }
    });
    setAsking(null);
    if (ok) {
      setDraft(null);
      setSaved(true);
      onChanged();
    }
  };

  const toggleSelling = async (): Promise<void> => {
    const ok = await selling.run(async () => {
      await api.setCatalogCommerce(tenantId, item.item_id, { active: !item.active });
    });
    if (ok) {
      setAsking(null);
      onChanged();
    }
  };

  const onSave = (): void => {
    if (commerceEditable) void save();
    else setAsking('save');
  };

  const hersNow = commerceEditable ? undefined : t('catalog.editor.ownersNow');
  const labels = { item: item.item_id };
  const name = textOf(item.name, locale) || item.item_id;

  return (
    <Panel
      className="catalog-editor"
      title={name}
      actions={
        <>
          {websiteSwitch !== null && !current.onWebsite ? (
            <Chip>{t('catalog.website.off')}</Chip>
          ) : null}
          {item.active ? (
            <Chip tone="ok">{t('catalog.editor.onSale')}</Chip>
          ) : (
            <Chip>{t('catalog.editor.notOnSale')}</Chip>
          )}
        </>
      }
    >
      <div data-item={item.item_id} className="catalog-editor__body">
        <p className={commerceEditable ? 'catalog-fence catalog-fence--open' : 'catalog-fence'}>
          {commerceEditable ? t('catalog.fence.settingUp') : t('catalog.fence.liveNow')}
        </p>

        <div className="catalog-fields">
          <Field label={t('catalog.name')} {...(hersNow === undefined ? {} : { hint: hersNow })}>
            <input
              aria-label={t('catalog.nameOf', labels)}
              value={value.name}
              disabled={!commerceEditable}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </Field>

          <Field
            label={t('catalog.price')}
            {...(optioned
              ? { hint: t('catalog.options.headline') }
              : hersNow === undefined
                ? {}
                : { hint: hersNow })}
          >
            <input
              inputMode="decimal"
              aria-label={t('catalog.priceOf', labels)}
              value={value.price}
              disabled={!commerceEditable || optioned}
              readOnly={optioned}
              aria-invalid={!priceValid}
              onChange={(e) => patch({ price: e.target.value })}
            />
          </Field>

          {item.scheduling === null ? null : (
            <Field
              label={t('catalog.duration')}
              {...(optioned
                ? { hint: t('catalog.options.headline') }
                : hersNow === undefined
                  ? {}
                  : { hint: hersNow })}
            >
              <input
                type="number"
                aria-label={t('catalog.durationOf', labels)}
                value={value.duration}
                disabled={!commerceEditable || optioned}
                readOnly={optioned}
                aria-invalid={!durationValid}
                onChange={(e) => patch({ duration: e.target.value })}
              />
            </Field>
          )}

          {item.scheduling === null ? null : (
            <>
              {/*
               * The gap either side, with the meaning of a blank box printed as
               * the hint: "house default (15)". Since 0064 a `0` is "none", and
               * the only way to say "the house's" is to leave the box empty.
               */}
              <Field
                label={t('catalog.bufferBefore')}
                hint={
                  hersNow ?? t('catalog.bufferHint', { minutes: houseBuffers?.before_min ?? null })
                }
              >
                <input
                  type="number"
                  min={0}
                  aria-label={t('catalog.bufferBeforeOf', labels)}
                  placeholder={t('catalog.houseDefault', {
                    minutes: houseBuffers?.before_min ?? null,
                  })}
                  value={value.bufferBefore}
                  disabled={!commerceEditable}
                  aria-invalid={!bufferValid(value.bufferBefore)}
                  onChange={(e) => patch({ bufferBefore: e.target.value })}
                />
              </Field>
              <Field
                label={t('catalog.bufferAfter')}
                hint={
                  hersNow ?? t('catalog.bufferHint', { minutes: houseBuffers?.after_min ?? null })
                }
              >
                <input
                  type="number"
                  min={0}
                  aria-label={t('catalog.bufferAfterOf', labels)}
                  placeholder={t('catalog.houseDefault', {
                    minutes: houseBuffers?.after_min ?? null,
                  })}
                  value={value.bufferAfter}
                  disabled={!commerceEditable}
                  aria-invalid={!bufferValid(value.bufferAfter)}
                  onChange={(e) => patch({ bufferAfter: e.target.value })}
                />
              </Field>
            </>
          )}

          <Field label={t('catalog.category')} hint={t('catalog.editor.headingHint')}>
            <input
              aria-label={t('catalog.categoryOf', labels)}
              value={value.category}
              onChange={(e) => patch({ category: e.target.value })}
            />
          </Field>

          <Field label={t('catalog.sortOrder')} hint={t('catalog.sortOrderHint')}>
            <input
              type="number"
              aria-label={t('catalog.sortOrderOf', labels)}
              value={value.sortOrder}
              onChange={(e) => patch({ sortOrder: e.target.value })}
            />
          </Field>

          {item.retail === null ? null : (
            <Field label={t('catalog.brand')} hint={t('catalog.brandHint')}>
              <input
                aria-label={t('catalog.brandOf', labels)}
                value={value.brand}
                onChange={(e) => patch({ brand: e.target.value })}
              />
            </Field>
          )}
        </div>

        {websiteSwitch === null ? null : (
          <Field
            label={t('catalog.website.show')}
            hint={
              websiteSwitch === 'product'
                ? t('catalog.website.productHint')
                : (hersNow ?? t('catalog.website.serviceHint'))
            }
          >
            <Switch
              checked={value.onWebsite}
              label={t('catalog.website.show')}
              disabled={websiteSwitch === 'service' && !commerceEditable}
              onChange={(next) => patch({ onWebsite: next })}
            />
          </Field>
        )}

        <Field label={t('catalog.editor.description')} hint={t('catalog.descriptionHint')}>
          <textarea
            rows={3}
            aria-label={t('catalog.descriptionOf', labels)}
            value={value.description}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </Field>

        <div className="row-actions">
          <button
            type="button"
            className="button button--primary button--sm"
            disabled={!dirty || !valid || action.busy}
            onClick={onSave}
          >
            {action.busy ? t('common.saving') : t('catalog.save')}
          </button>
          {dirty ? (
            <button
              type="button"
              className="button button--quiet button--sm"
              onClick={() => {
                setDraft(null);
                action.clear();
              }}
            >
              {t('catalog.editor.discard')}
            </button>
          ) : null}
          <span className="catalog-editor__spacer" />
          <button
            type="button"
            className="button button--quiet button--sm"
            disabled={!commerceEditable || selling.busy}
            title={hersNow}
            onClick={() => setAsking('selling')}
          >
            {item.active ? t('catalog.editor.stopSelling') : t('catalog.editor.sellAgain')}
          </button>
        </div>

        {asking === null && action.failure !== null ? <Failure failure={action.failure} /> : null}
        {selling.failure !== null && asking === null ? <Failure failure={selling.failure} /> : null}
        {saved ? <p className="ok">{t('catalog.saved')}</p> : null}

        {/*
         * Only a scheduled service has lengths or add-ons. A dish's sizes are
         * deliberately absent: `priceOrder` does not read `item_options`, so a
         * "Large" seeded onto a dish today would be charged at the small price.
         */}
        {item.kind === 'scheduled' ? (
          <>
            <ItemOptions
              tenantId={tenantId}
              item={item}
              locale={locale}
              commerceEditable={commerceEditable}
              onChanged={() => {
                setSaved(false);
                onChanged();
              }}
            />
            <ItemAddOns
              tenantId={tenantId}
              item={item}
              siblings={siblings}
              locale={locale}
              currency={currency}
              commerceEditable={commerceEditable}
              onChanged={() => {
                setSaved(false);
                onChanged();
              }}
            />
          </>
        ) : null}

        <div className="catalog-block">
          <div className="catalog-block__row">
            <h4 className="catalog-block__title">{t('catalog.photosTitle')}</h4>
            <span className="muted">
              {item.images.length === 0
                ? t('catalog.editor.noPhotos')
                : t('catalog.editor.photoCount', { count: item.images.length })}
            </span>
            <button
              type="button"
              className="button button--quiet button--sm"
              aria-expanded={photos}
              onClick={() => setPhotos(!photos)}
            >
              {photos ? t('catalog.editor.closePhotos') : t('catalog.editor.editPhotos')}
            </button>
          </div>
          {photos ? (
            <ItemPhotos
              tenantId={tenantId}
              item={item}
              locale={locale}
              uploadsConfigured={uploadsConfigured}
              onChanged={() => {
                setSaved(false);
                onChanged();
              }}
            />
          ) : null}
        </div>
      </div>

      {asking === 'save' ? (
        <Confirm
          tone="primary"
          title={t('catalog.confirm.saveTitle', { item: name })}
          consequence={t('catalog.confirm.saveLive')}
          confirmLabel={t('catalog.confirm.saveVerb')}
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void save()}
          onCancel={() => setAsking(null)}
        />
      ) : null}
      {asking === 'selling' ? (
        <Confirm
          tone={item.active ? 'danger' : 'primary'}
          title={
            item.active
              ? t('catalog.confirm.stopTitle', { item: name })
              : t('catalog.confirm.againTitle', { item: name })
          }
          consequence={item.active ? t('catalog.confirm.stop') : t('catalog.confirm.again')}
          confirmLabel={
            item.active ? t('catalog.editor.stopSelling') : t('catalog.editor.sellAgain')
          }
          busy={selling.busy}
          failure={selling.failure}
          onConfirm={() => void toggleSelling()}
          onCancel={() => {
            selling.clear();
            setAsking(null);
          }}
        />
      ) : null}
    </Panel>
  );
}
